/**
 * <product-card-hover-video>
 *
 * Wraps the secondary-media slot on a product card when that slot holds a
 * playable video (native Shopify-hosted MP4 or YouTube/Vimeo embedded via
 * Shopify's <video-media>). Plays the video on pointer/focus hover of the
 * enclosing .product-card__figure; pauses + rewinds when both pointer and
 * focus have left.
 *
 * Ported behavior from Else Lingerie's setupProductCardVideoHover (originally
 * a private method on their ProductCard custom element). Kept as a standalone
 * custom element here so we don't touch the Prestige theme.js bundle (which
 * would be overwritten on theme upgrades). connectedCallback auto-fires when
 * the element is inserted, so SPA city-swaps, AJAX-filtered grids, and
 * infinite scroll all attach hover wiring without a MutationObserver.
 *
 * Pointer + focus state are tracked separately so that a transient focus
 * loss (e.g. wishlist save triggers <gw-wishlist-mount> to recreate its
 * button, briefly orphaning focus) does NOT pause the video while the user
 * is still hovering. We only pause when both pointerInside and focusInside
 * have flipped false.
 *
 * Bails out early on:
 *   - prefers-reduced-motion: reduce    (accessibility)
 *   - pointer: coarse (no fine pointer) (touch devices — no hover gesture)
 *
 * Required ancestor:  .product-card__figure
 * Required children:  <video-media>  (one or more, rendered inside this element by product-card.liquid)
 */
if (!customElements.get('product-card-hover-video')) {
  class ProductCardHoverVideo extends HTMLElement {
    constructor() {
      super();
      this.pointerInside = false;
      this.focusInside = false;
    }

    connectedCallback() {
      // Re-init safely if the element moved in the DOM.
      if (this.abort) this.abort.abort();

      // Touch + reduced-motion: leave the secondary slot as a static poster
      // (the existing CSS hover swap still works; we just never play).
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!window.matchMedia('(pointer: fine)').matches) return;

      this.figure = this.closest('.product-card__figure');
      if (!this.figure) return;

      this.videoMedias = this.querySelectorAll('video-media');
      if (!this.videoMedias.length) return;

      this.abort = new AbortController();
      const { signal } = this.abort;

      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);

      this.figure.addEventListener('pointerenter', this.onPointerEnter, { signal });
      this.figure.addEventListener('pointerleave', this.onPointerLeave, { signal });
      this.figure.addEventListener('focusin', this.onFocusIn, { signal });
      this.figure.addEventListener('focusout', this.onFocusOut, { signal });
    }

    disconnectedCallback() {
      if (this.abort) {
        this.abort.abort();
        this.abort = null;
      }
      this.pointerInside = false;
      this.focusInside = false;
      // Defensive: stop playback if the element is yanked mid-play.
      if (this.videoMedias && this.videoMedias.length) {
        this.pause();
      }
    }

    onPointerEnter() {
      this.pointerInside = true;
      this.play();
    }

    onPointerLeave() {
      this.pointerInside = false;
      if (!this.focusInside) this.pause();
    }

    onFocusIn() {
      this.focusInside = true;
      this.play();
    }

    onFocusOut(event) {
      // Focus moving within the figure (e.g. between swatches and the wishlist
      // button) keeps focusInside true — early-return without flipping state.
      if (this.figure.contains(event.relatedTarget)) return;

      this.focusInside = false;
      // Critical fix: if the pointer is still hovering, keep playing. This
      // covers the wishlist-save case where <gw-wishlist-mount> removes the
      // focused button mid-click, firing focusout with relatedTarget=null
      // while the mouse is unmoved.
      if (!this.pointerInside) this.pause();
    }

    play() {
      this.videoMedias.forEach((vm) => {
        const video = vm.querySelector('video');
        if (video) video.preload = 'auto';
        // <video-media>.play() handles native + external (lazy-loads the
        // YouTube/Vimeo iframe from the inner <template> on first call).
        // play() returns a Promise that can reject on autoplay policy
        // collisions — swallow silently so a single failed card doesn't
        // break the others on the page.
        try {
          const result = vm.play();
          if (result && typeof result.then === 'function') {
            result.catch(() => {});
          }
        } catch (_) {
          /* noop */
        }
      });
    }

    pause() {
      this.videoMedias.forEach((vm) => {
        try { vm.pause(); } catch (_) { /* noop */ }
        const video = vm.querySelector('video');
        if (video) {
          video.currentTime = 0;
          video.preload = 'none';
        }
        vm.removeAttribute('playing');
      });
    }
  }

  customElements.define('product-card-hover-video', ProductCardHoverVideo);
}
